/*!
  =========================================================
  * Muse Ant Design Dashboard - v1.0.0
  =========================================================
  * Product Page: https://www.creative-tim.com/product/muse-ant-design-dashboard
  * Copyright 2021 Creative Tim (https://www.creative-tim.com)
  * Licensed under MIT (https://github.com/creativetimofficial/muse-ant-design-dashboard/blob/main/LICENSE.md)
  * Coded by Creative Tim
  =========================================================
  * The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { Layout } from 'antd';
import { openNotificationWithIcon } from 'app/components/PopupNotification';
import {
  setLoginScreen,
  setSelectProduct,
  setUserDetail,
  setUserPermissionWhenReloadPage,
} from 'app/pages/LoginPage/auth.slice';
import { getUserDetailAPI, getUserModulePermissionAPI } from 'app/services/api/settingAdmin';
import { lowerCaseKeys } from 'app/services/helper';
import { useAppDispatch, useAppSelector } from 'app/store';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import './Layout.scss';
import Sidenav from './Sidenav';
import { useAutoRefreshToken } from 'app/hooks/useAutoRefreshToken.hook';
import { PopupConfirmSavePage } from 'app/pages/DecisionTable/PopupConfirm/PopupConfirmSavePage';
import {
  triggerIsSaveDecisionTable,
  triggerIsSaveRACIDecisionTable,
} from 'app/pages/DecisionTable/DecisionTable.slice';
import { clearLocalStorage, startAutoCall, stopAutoCall } from 'app/constants';
import { triggerWarningEditDefect } from 'app/pages/Bugs/Bugs.slice';
const { Header: AntHeader, Content, Sider } = Layout;

function Main({ children }) {
  const dispatch = useAppDispatch();
  const isRunningDebugRunner = useAppSelector(state => state?.enginuityWorkbench?.isRunningDebugRunner);
  const isBreakingPoint = useAppSelector(state => state?.enginuityWorkbench?.isBreakingPoint);
  const isEdittingPageLayout = useAppSelector(state => state.AI.isEdittingPageLayout);
  const isShowBlockDecisionTable = useAppSelector(state => state.decisionTableSlice.isShowBlockDecisionTable);
  const isShowBlockRACIDecisionTable = useAppSelector(state => state.decisionTableSlice.isShowBlockRACIDecisionTable);
  const warningEditDefect = useAppSelector(state => state.bugs.warningEditDefect);
  const loginScreen = useAppSelector(state => state?.auth.loginScreen);
  const user = useAppSelector(state => state.auth.user);
  const isUpdateProfile = useAppSelector(state => state.auth.isUpdateProfile);
  const { refreshToken, scheduleTokenRefresh } = useAutoRefreshToken();

  useEffect(() => {
    if ((isShowBlockDecisionTable || isShowBlockRACIDecisionTable) === false) {
      dispatch(triggerIsSaveDecisionTable(false));
      dispatch(triggerIsSaveRACIDecisionTable(false));
    }
  }, [dispatch, isShowBlockDecisionTable, isShowBlockRACIDecisionTable]);

  const sidenavColor = '#e9e9e9';
  const sidenavType = '#002060';
  let { pathname } = useLocation();
  pathname = pathname.replace('/', '');

  const miniSidebarClass = useMemo(() => {
    if (pathname === 'iframe') {
      return 'mini-sidebar';
    }
    return '';
  }, [pathname]);

  const handleWarningWhenEdittingPageLayout = () => {
    openNotificationWithIcon('warning', 'You must save or cancel editing the page layout before exit');
  };

  const handleWarningWhenEdittingDefect = () => {
    openNotificationWithIcon(
      'warning',
      'All fields (Execution Cycle, Test Case, Test Step) must be fully filled or all left blank.',
    );
    dispatch(triggerWarningEditDefect(false));
  };

  const handleShowPopupWhenEditDecisiontable = () => {
    // setIsOpenPopupSavePage(true);
    dispatch(triggerIsSaveDecisionTable(true));
    dispatch(triggerIsSaveRACIDecisionTable(true));
  };

  const getModulePermission = async () => {
    try {
      let res = await getUserModulePermissionAPI();
      let result = res?.data?.data?.map(item => lowerCaseKeys(item));
      dispatch(setUserPermissionWhenReloadPage(result));
    } catch (error) {
      openNotificationWithIcon('error', error?.message);
    }
  };

  const getUserDetail = async () => {
    try {
      const res = await getUserDetailAPI();
      dispatch(setUserDetail(res?.data));
    } catch (error) {}
  };

  React.useEffect(() => {
    getUserDetail();
  }, [isUpdateProfile]);

  useEffect(() => {
    getModulePermission();
  }, []);

  useEffect(() => {
    const handleInvalidToken = e => {
      const accessToken = JSON.parse(window.localStorage.getItem('accessToken'));
      if (!accessToken) {
        // localStorage.clear();
        clearLocalStorage();
        dispatch(setLoginScreen(1));
        dispatch(setSelectProduct(undefined));
        sessionStorage.clear();
        window.location.href = '/login';
      }
    };

    window.addEventListener('storage', handleInvalidToken);
    return function cleanup() {
      window.removeEventListener('storage', handleInvalidToken);
    };
  }, []);

  useEffect(() => {
    startAutoCall(); // bắt đầu khi app chạy

    return () => stopAutoCall(); // clear khi app unmount
  }, []);

  return (
    <Layout
      className={`layout-dashboard ${miniSidebarClass} ${pathname === 'profile' ? 'layout-profile' : ''}
      ${pathname === 'rtl' ? 'layout-dashboard-rtl' : ''}`}
      onContextMenu={e => e.preventDefault()}
      onClick={() => {
        const currentToken = localStorage.getItem('accessToken');
        scheduleTokenRefresh(currentToken);
      }}
    >
      <div
        className="blur-when-edit-page-layout"
        style={{ display: isEdittingPageLayout ? 'block' : 'none' }}
        onClick={handleWarningWhenEdittingPageLayout}
      />
      <div
        className="blur-when-edit-defect"
        style={{ display: warningEditDefect ? 'block' : 'none' }}
        onClick={handleWarningWhenEdittingDefect}
      />
      <div
        className="blur-when-edit-decision-table"
        style={{ display: isShowBlockDecisionTable ? 'block' : 'none' }}
        onClick={handleShowPopupWhenEditDecisiontable}
      />
      <div
        className="blur-x-when-edit-decision-table"
        style={{ display: isShowBlockDecisionTable || isShowBlockRACIDecisionTable ? 'block' : 'none' }}
        onClick={handleShowPopupWhenEditDecisiontable}
      />
      <div
        className="blur-x-when-edit-decision-table-2"
        style={{ display: isShowBlockDecisionTable || isShowBlockRACIDecisionTable ? 'block' : 'none' }}
        onClick={handleShowPopupWhenEditDecisiontable}
      />
      <AntHeader
        style={{
          position: 'fixed',
          zIndex: 100,
          width: '100%',
          pointerEvents: isRunningDebugRunner ? 'none' : 'unset',
        }}
      >
        <Header />
      </AntHeader>
      <Layout style={{ marginTop: 95 }}>
        <Sider
          breakpoint="lg"
          collapsedWidth="0"
          trigger={null}
          width={miniSidebarClass ? 100 : 250}
          theme="light"
          className={`sider-primary ant-layout-sider-primary ${sidenavType === '#fff' ? 'active-route' : ''}`}
          style={{
            background: sidenavType,
            pointerEvents: isRunningDebugRunner && !isBreakingPoint ? 'none' : 'unset',
          }}
        >
          <Sidenav color={sidenavColor} />
        </Sider>
        <Content className="content-ant" style={{ paddingTop: 10 }}>
          {children}
        </Content>
      </Layout>
    </Layout>
  );
}

export default Main;
